import { Container } from '@/components/ui/template/Container'
import { Fade } from '@/components/ui/transitions/Fade'
import { useSurveys } from '@/data-access/surveys/queries'
import { hoursToMs } from '@/utils/datetime/unitToMs'
import { EndedPollInfo, OngoingPollInfo, UpcomingPollInfo } from './molecules'
import { match } from 'ts-pattern'
import { ButtonLink } from '@/components/ui/Button'
import { waitForNextMinedBlocks } from '@/utils/wagmi/wait-for-block'

export function PollMapSection() {
	const { data: upcomingSurvey, refetch: refetchUpcomingSurvey } = useSurveys({
		page: 1,
		perPage: 1,
		state: ['upcoming'],
		orderBy: 'startDate',
		direction: 'asc'
	})

	const { data: ongoingSurvey, refetch: refetchOngoingSurvey } = useSurveys({
		page: 1,
		perPage: 3,
		state: ['ongoing'],
		orderBy: 'endDate',
		direction: 'asc'
	})

	const { data: endedSurveyData, refetch: refetchEndedSurveyData } = useSurveys({
		page: 1,
		perPage: 3,
		staleTime: hoursToMs(1),
		state: ['ended'],
		orderBy: 'endDate',
		direction: 'desc'
	})

	const flatOngoingSurveys = ongoingSurvey?.pages.flatMap(page => page.surveys) ?? []

	const mergedOngoingAndUpcomingSurveys = [
		...(upcomingSurvey?.pages.flatMap(page => page.surveys) ?? []),
		...flatOngoingSurveys
	].slice(0, 3)

	const availableEndedColumns = 3 - mergedOngoingAndUpcomingSurveys.length

	const endedSurveys = (endedSurveyData?.pages.flatMap(page => page.surveys) ?? []).slice(0, availableEndedColumns)

	async function onTimerEnd() {
		// Wait for 3 blocks to provide some leeway for the indexer to catch-up
		await waitForNextMinedBlocks(3)

		await Promise.all([refetchUpcomingSurvey(), refetchOngoingSurvey(), refetchEndedSurveyData()])
	}

	return (
		<Container className="bg-green-20">
			<Container.Content>
				<Fade variant="in">
					<div className="grid grid-cols-1 grid-rows-[1fr_auto] md:grid-cols-3 gap-y-16 md:gap-y-8 md:gap-x-6 place-items-center">
						{endedSurveys.length > 0 && (
							<div
								className="flex flex-col md:flex-row-reverse gap-16 md:gap-6 md:order-1 order-3 w-full md:[grid-column:span_var(--ended-col-span)/span_var(--ended-col-span)]"
								style={{ '--ended-col-span': availableEndedColumns } as React.CSSProperties}
							>
								{endedSurveys.map(survey => (
									<EndedPollInfo key={survey.surveyContract} survey={survey} />
								))}
							</div>
						)}

						{mergedOngoingAndUpcomingSurveys.map(survey => {
							return match(survey.status)
								.with('ongoing', () => (
									<OngoingPollInfo
										key={survey.surveyContract}
										survey={survey}
										className="md:order-2"
										onTimerEnd={onTimerEnd}
									/>
								))
								.with('upcoming', () => (
									<UpcomingPollInfo
										key={survey.surveyContract}
										survey={survey}
										className="md:order-3"
										onTimerEnd={onTimerEnd}
									/>
								))
								.run()
						})}

						<ButtonLink
							to="/pepper-inc#polls"
							variant="danger"
							className="py-5 px-10 order-4 md:col-span-3"
						>
							{flatOngoingSurveys.length > 0 ? 'Vote now' : 'View Polls'}
						</ButtonLink>
					</div>
				</Fade>
			</Container.Content>
		</Container>
	)
}
